import React from 'react';
import { Button, Col, Form, InputNumber, Row } from 'antd';
import { useFormMapper } from 'common/helpers/form.helper';
import { requireText } from 'common/const/common.const';
import { WarningNotification } from 'common/components/WarningNotification';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { setBitecoinSettingsModelParams, updateBitecoinSettings } from 'app/store/reducers/bitecoin.reducer';

interface IComponentProps {
  setIsEditMode: (value: boolean) => void;
}

export const GeneralBitecoinSettingsForm: React.FC<IComponentProps> = ({ setIsEditMode }) => {
  const dispatch = useAppDispatch();
  const { bitecoinSettingsModel, bitecoinSettingsModelParams, bitecoinSettingsModelError, bitecoinSettingsModelLoading } = useAppSelector(state => state.bitecoin);
  const { fields } = useFormMapper(
    ['pesoToPointRatio', 'daysBeforeExpiration', 'walletLimit'],
    bitecoinSettingsModel,
    bitecoinSettingsModelParams,
    bitecoinSettingsModelError
  );

  const saveBitecoinSettings = (values: any) => {
    dispatch(updateBitecoinSettings(values, () => setIsEditMode(false)));
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    dispatch(setBitecoinSettingsModelParams(null));
  };

  return (
    <Form onFinish={saveBitecoinSettings} fields={fields} layout="vertical" className="mt-5 mb-5">
      <Row>
        <Col span={24}>
          <span className="basic__text_title" title="Peso-to-point ratio">
            Peso-to-point ratio
          </span>
        </Col>
        <Col span={12} className="mt-3 mb-5">
          <span
            className="color-grayDark fs-xxs font-family__default"
            title="Set a peso-to-point ratio (e.g., 1 Bitecoin for every 10 pesos spent)."
          >
            Set a peso-to-point ratio (e.g., 1 Bitecoin for every 10 pesos spent).
          </span>
        </Col>
      </Row>

      <Col span={12}>
        <Form.Item
          rules={[
            {
              required: true,
              message: requireText,
            },
            {
              type: 'number',
              min: 1,
              max: 100,
              message: 'Value must be equal to 1-100',
            },
          ]}
          label="Peso Amount for 1 Bitecoin"
          name="pesoToPointRatio"
        >
          <InputNumber
            type="number"
            name="pesoToPointRatio"
            disabled={bitecoinSettingsModelLoading}
            max={100}
            min={1}
            precision={0}
            placeholder="0"
          />
        </Form.Item>
      </Col>

      <WarningNotification text='Changing the conversion rate does not affect Bitecoins that have already been earned.' />

      <Row className="mt-5">
        <Col span={24}>
          <span className="basic__text_title" title="Peso-to-point ratio">
            Expiry Term
          </span>
        </Col>
        <Col span={12} className="mt-3 mb-5">
          <span
            className="color-grayDark fs-xxs font-family__default"
            title="Set how many days after earning all of the customer’s Bitecoins will expire."
          >
            Set how many days after earning all of the customer’s Bitecoins will expire.
          </span>
        </Col>
      </Row>

      <Col span={12}>
        <Form.Item
          rules={[
            {
              required: true,
              message: requireText,
            },
            {
              type: 'number',
              min: 1,
              max: 365,
              message: 'Value must be equal to 1-365',
            },
          ]}
          label="Days Until Expiry"
          name="daysBeforeExpiration"
        >
          <InputNumber
            type="number"
            name="daysBeforeExpiration"
            disabled={bitecoinSettingsModelLoading}
            max={365}
            min={1}
            precision={0}
            placeholder="0"
          />
        </Form.Item>
      </Col>

      <Row>
        <Col span={24}>
          <span className="basic__text_title" title="Peso-to-point ratio">
            Wallet Limit
          </span>
        </Col>
        <Col span={12} className="mt-3 mb-5">
          <span
            className="color-grayDark fs-xxs font-family__default"
            title="Set the maximum number of Bitecoins a customer can hold. Once this limit is reached, no additional Bitecoins will be awarded."
          >
            Set the maximum number of Bitecoins a customer can hold. Once this limit is reached, no additional Bitecoins will be awarded.
          </span>
        </Col>
      </Row>

      <Col span={12}>
        <Form.Item
          rules={[
            {
              required: true,
              message: requireText,
            },
            {
              type: 'number',
              min: 1,
              max: 10000,
              message: 'Value must be equal to 1-10000',
            },
          ]}
          label="Maximum Bitecoin Limit"
          name="walletLimit"
        >
          <InputNumber
            type="number"
            name="walletLimit"
            disabled={bitecoinSettingsModelLoading}
            max={10000}
            min={1}
            precision={0}
            placeholder="0"
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Row>
          <Col>
            <Form.Item className="mb-0 mr-5">
              <Button
                htmlType="submit"
                type="primary"
                block
                disabled={bitecoinSettingsModelLoading}
                loading={bitecoinSettingsModelLoading}
                title='Save'
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Button
              block
              disabled={bitecoinSettingsModelLoading}
              loading={bitecoinSettingsModelLoading}
              title="Cancel"
              onClick={cancelEdit}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Col>
    </Form>
  );
};
